import { useModalStatusMessage } from '@/hooks';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import NumericTextField from '@components/NumericTextField';
import ModalActions from '@components/modal/ModalActions';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Box, DialogContent, Divider } from '@mui/material';
import { useEffect, type FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import UPDATE_DGA_MAX_FLOW from '@features/dga/graphql/mutations/updateDgaMaxFlow';
import GET_DGA_MAX_FLOW from '@features/dga/graphql/queries/getDGAMaxFlow';
import { DGATabProps } from './tabProps';

interface FormValues {
  maxFlow: string;
}

const MaxFlowTab: FC<DGATabProps> = ({ deviceId, onClose, setIsDirty }) => {
  const { _ } = useLingui();
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();
  const { data } = useSuspenseQuery(GET_DGA_MAX_FLOW, {
    variables: {
      deviceId,
    },
  });
  const [updateMaxFlow] = useMutation(UPDATE_DGA_MAX_FLOW, {
    onCompleted() {
      openModalSuccessMessage(_(msg`Caudal máximo actualizado correctamente`));
    },
    onError(error) {
      openModalErrorMessage(_(msg`No se pudo actualizar el caudal máximo`) + ' ' + error.message);
    },
  });

  const { reset, formState, ...methods } = useForm<FormValues>({
    defaultValues: {
      maxFlow: data.device.dga?.configuration.maxFlow?.toString() ?? '',
    },
  });

  useEffect(() => {
    setIsDirty(formState.isDirty);
  }, [formState.isDirty, setIsDirty]);

  useEffect(() => {
    reset({
      maxFlow: data.device.dga?.configuration.maxFlow?.toString() ?? '',
    });
  }, [data, reset]);

  const onSubmit = methods.handleSubmit(async (data) => {
    const maxFlow = parseFloat(data.maxFlow);

    await updateMaxFlow({
      variables: {
        deviceId,
        maxFlow: Number.isNaN(maxFlow) ? null : maxFlow,
      },
    });
  });

  return (
    <form onSubmit={onSubmit}>
      <DialogContent sx={{ height: 360 }}>
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            md: '1fr 1fr',
          }}
          gap={2}
        >
          <Controller
            control={methods.control}
            name="maxFlow"
            render={({ field, fieldState }) => (
              <NumericTextField
                {...field}
                label={_(msg`Caudal máximo en L/s`)}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                autoFocus
              />
            )}
          />
        </Box>
      </DialogContent>
      <Divider />
      <ModalActions
        onClose={onClose}
        onSubmit="submit"
        submitLoading={formState.isSubmitting}
        dirtyForm={formState.isDirty}
        onResetForm={() => reset()}
      />
    </form>
  );
};

export default MaxFlowTab;
